import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { DollarSign, Download, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const currency = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MyPayrollPage() {
  const [paystubs, setPaystubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/employee-portal/payroll/paystubs`);
        setPaystubs(r.data.paystubs || r.data || []);
      } catch(e){ if(e?.response?.status !== 403) toast.error('Failed to load paystubs'); }
      finally { setLoading(false); }
    })();
  }, []);

  const ytd = paystubs.reduce((acc, p) => acc + (p.net_pay || 0), 0);
  const ytdGross = paystubs.reduce((acc, p) => acc + (p.gross_pay || 0), 0);
  const ytdTax = paystubs.reduce((acc, p) => acc + (p.tax_withheld || p.taxes || 0), 0);

  return (
    <div className="min-h-screen p-6 bg-gray-50" data-testid="employee-payroll-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Paystubs & YTD</h1>
          <p className="text-gray-600 mt-1">Your compensation history, computed from your attendance and benefits.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><div className="text-xs uppercase text-gray-500">YTD Gross</div><TrendingUp className="h-4 w-4 text-emerald-500" /></div>
            <div className="text-2xl font-bold text-gray-900" data-testid="ytd-gross">{currency(ytdGross)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><div className="text-xs uppercase text-gray-500">YTD Tax Withheld</div><DollarSign className="h-4 w-4 text-red-400" /></div>
            <div className="text-2xl font-bold text-gray-900">{currency(ytdTax)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center justify-between mb-1"><div className="text-xs uppercase text-gray-500">YTD Net</div><DollarSign className="h-4 w-4 text-emerald-500" /></div>
            <div className="text-2xl font-bold text-emerald-600" data-testid="ytd-net">{currency(ytd)}</div>
          </CardContent></Card>
        </div>

        <Card>
          <CardHeader><CardTitle>All paystubs</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-gray-500">Loading…</div> :
             paystubs.length === 0 ? <div className="text-gray-500 italic">No paystubs yet. They appear here after each payroll run.</div> :
             <div className="overflow-x-auto"><table className="w-full text-sm">
               <thead className="text-xs uppercase text-gray-500 border-b border-gray-200">
                 <tr><th className="text-left py-2">Period</th><th className="text-right">Gross</th><th className="text-right">Tax</th><th className="text-right">Net</th><th></th></tr>
               </thead>
               <tbody className="divide-y divide-gray-100">
                 {paystubs.map((p, i) => (
                   <tr key={i} className="hover:bg-gray-50">
                     <td className="py-2">{p.period_start} → {p.period_end}<div className="text-xs text-gray-500">Issued {p.issued_at || p.date || '—'}</div></td>
                     <td className="text-right">{currency(p.gross_pay)}</td>
                     <td className="text-right text-red-500">−{currency(p.tax_withheld || p.taxes || 0)}</td>
                     <td className="text-right font-semibold text-emerald-600">{currency(p.net_pay)}</td>
                     <td className="text-right">{p.download_url && <a href={p.download_url} className="text-emerald-600 inline-flex items-center gap-1"><Download className="h-3 w-3" />PDF</a>}</td>
                   </tr>
                 ))}
               </tbody>
             </table></div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
